import React, { useEffect, useState } from 'react'
import { Shield, FileCheck, AlertTriangle, Calendar, CheckCircle, XCircle, Clock, TrendingUp, Loader2 } from 'lucide-react'
import { DataTable } from '@components/common'
import { RiskExposureReport } from '@components/features/dashboard/widgets'
import dashboardService from '@services/dashboardService'
import './ComplianceDashboard.css'

// Simple Card component for dashboard sections
const Card = ({ className = '', children }) => (
  <div className={`dashboard-card ${className}`}>{children}</div>
)

/**
 * ComplianceDashboard - Senior Management Compliance Overview
 *
 * A read-only, high-level dashboard for senior management to quickly
 * understand the organization-wide compliance posture.
 *
 * Sections:
 * 1. Compliance Status Summary (Top Section)
 * 2. Project/Engagement Overview (Table)
 * 3. Evidence Availability Snapshot
 * 4. Audit/Readiness Indicator
 */
const ComplianceDashboard = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const response = await dashboardService.getManagementOverview()
      setData(response.data)
      setError(null)
    } catch (err) {
      console.error('Failed to load dashboard:', err)
      setError('Failed to load compliance dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDateShort = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getComplianceColor = (percentage) => {
    if (percentage >= 75) return 'var(--color-success)'
    if (percentage >= 50) return 'var(--color-warning)'
    return 'var(--color-danger)'
  }

  const getStatusIndicator = (status) => {
    const config = {
      green: { color: 'var(--color-success)', label: 'Good' },
      amber: { color: 'var(--color-warning)', label: 'Attention' },
      red: { color: 'var(--color-danger)', label: 'At Risk' },
    }
    return config[status] || config.amber
  }

  const getReadinessConfig = (status) => {
    const config = {
      ready: { color: 'var(--color-success)', label: 'Ready', icon: CheckCircle },
      partial: { color: 'var(--color-warning)', label: 'Partial', icon: Clock },
      not_ready: { color: 'var(--color-danger)', label: 'Not Ready', icon: AlertTriangle },
    }
    return config[status] || config.not_ready
  }

  if (loading) {
    return (
      <div className="compliance-dashboard">
        <div className="compliance-dashboard__loading">
          <Loader2 size={48} className="spinner" />
          <p>Loading compliance dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="compliance-dashboard">
        <div className="compliance-dashboard__error">
          <AlertTriangle size={48} />
          <h3>Unable to Load Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn--primary">
            Retry
          </button>
        </div>
      </div>
    )
  }

  const { compliance_summary, projects, evidence_availability, audit_readiness } = data || {}

  // Project table columns - Note: DataTable render receives (row, value)
  const projectColumns = [
    {
      key: 'name',
      label: 'Project Name',
      sortable: true,
      render: (row, value) => <span className="project-name">{value}</span>,
    },
    {
      key: 'compliance_status',
      label: 'Status',
      sortable: true,
      render: (row, value) => {
        const status = getStatusIndicator(value)
        return (
          <div className="status-indicator">
            <span
              className="status-dot"
              style={{ backgroundColor: status.color }}
              title={`${row.progress}% Complete`}
            />
            <span className="status-label">{status.label}</span>
          </div>
        )
      },
    },
    {
      key: 'progress',
      label: 'Progress',
      sortable: true,
      render: (row, value) => (
        <div className="progress-cell">
          <div className="progress-bar-mini">
            <div
              className="progress-bar-mini__fill"
              style={{
                width: `${value}%`,
                backgroundColor: getComplianceColor(value),
              }}
            />
          </div>
          <span className="progress-value">{value}%</span>
        </div>
      ),
    },
  ]

  return (
    <div className="compliance-dashboard">
      {/* Page Header */}
      <div className="compliance-dashboard__header">
        <div className="header-content">
          <h1>Compliance Dashboard</h1>
          <p className="header-subtitle">Organization-wide compliance posture overview</p>
        </div>
        <div className="header-actions">
          <span className="last-updated">
            Last updated: {formatDate(compliance_summary?.last_updated)}
          </span>
        </div>
      </div>

      {/* Section 1: Compliance Status Summary */}
      <Card className="summary-card">
        <div className="summary-card__content">
          {/* Overall Compliance Indicator */}
          <div className="compliance-gauge">
            <div
              className="gauge-circle"
              style={{
                '--progress': compliance_summary?.overall_compliance || 0,
                '--color': getComplianceColor(compliance_summary?.overall_compliance || 0),
              }}
            >
              <div className="gauge-inner">
                <span className="gauge-value">{compliance_summary?.overall_compliance || 0}%</span>
                <span className="gauge-label">Overall Compliance</span>
              </div>
            </div>
          </div>

          {/* Frameworks Covered */}
          <div className="frameworks-section">
            <h3>
              <Shield size={18} />
              Standards & Frameworks
            </h3>
            <div className="frameworks-list">
              {compliance_summary?.frameworks?.length > 0 ? (
                compliance_summary.frameworks.map((framework) => (
                  <span key={framework.id} className="framework-badge">
                    {framework.title}
                  </span>
                ))
              ) : (
                <span className="no-frameworks">No frameworks configured</span>
              )}
            </div>
          </div>

          {/* Last Updated */}
          <div className="updated-section">
            <div className="updated-info">
              <Clock size={16} />
              <span>Records Updated</span>
            </div>
            <span className="updated-time">{formatDate(compliance_summary?.last_updated)}</span>
          </div>
        </div>
      </Card>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Section 2: Project Overview Table */}
        <Card className="projects-card">
          <div className="card-header">
            <h2>
              <FileCheck size={20} />
              Project Overview
            </h2>
            <span className="project-count">{projects?.total_count || 0} Projects</span>
          </div>
          <div className="projects-table-container">
            {projects?.items?.length > 0 ? (
              <DataTable
                columns={projectColumns}
                data={projects.items}
                pageSize={10}
                showPagination={projects.items.length > 10}
                emptyMessage="No projects found"
              />
            ) : (
              <div className="empty-state">
                <FileCheck size={32} />
                <p>No projects available</p>
              </div>
            )}
          </div>
        </Card>

        {/* Right Column: Evidence & Audit Readiness */}
        <div className="right-column">
          {/* Section 3: Evidence Availability */}
          <Card className="evidence-card">
            <div className="card-header">
              <h2>
                <FileCheck size={20} />
                Evidence Availability
              </h2>
            </div>
            <div className="evidence-grid">
              <div className="evidence-item">
                <div className={`evidence-status ${evidence_availability?.policies ? 'available' : 'unavailable'}`}>
                  {evidence_availability?.policies ? <CheckCircle size={20} /> : <XCircle size={20} />}
                </div>
                <span className="evidence-label">Policies</span>
                <span className={`evidence-value ${evidence_availability?.policies ? 'yes' : 'no'}`}>
                  {evidence_availability?.policies ? 'Available' : 'Not Available'}
                </span>
              </div>

              <div className="evidence-item">
                <div className={`evidence-status ${evidence_availability?.procedures ? 'available' : 'unavailable'}`}>
                  {evidence_availability?.procedures ? <CheckCircle size={20} /> : <XCircle size={20} />}
                </div>
                <span className="evidence-label">Procedures</span>
                <span className={`evidence-value ${evidence_availability?.procedures ? 'yes' : 'no'}`}>
                  {evidence_availability?.procedures ? 'Available' : 'Not Available'}
                </span>
              </div>

              <div className="evidence-item">
                <div className={`evidence-status ${evidence_availability?.records_logs ? 'available' : 'unavailable'}`}>
                  {evidence_availability?.records_logs ? <CheckCircle size={20} /> : <XCircle size={20} />}
                </div>
                <span className="evidence-label">Records / Logs</span>
                <span className={`evidence-value ${evidence_availability?.records_logs ? 'yes' : 'no'}`}>
                  {evidence_availability?.records_logs ? 'Available' : 'Not Available'}
                </span>
              </div>

              <div className="evidence-item">
                <div className={`evidence-status ${evidence_availability?.training ? 'available' : 'unavailable'}`}>
                  {evidence_availability?.training ? <CheckCircle size={20} /> : <XCircle size={20} />}
                </div>
                <span className="evidence-label">Training Evidence</span>
                <span className={`evidence-value ${evidence_availability?.training ? 'yes' : 'no'}`}>
                  {evidence_availability?.training ? 'Available' : 'Not Available'}
                </span>
              </div>
            </div>
          </Card>

          {/* Section 4: Audit Readiness */}
          <Card className="audit-card">
            <div className="card-header">
              <h2>
                <TrendingUp size={20} />
                Audit Readiness
              </h2>
            </div>
            <div className="audit-content">
              {/* Evidence Completeness */}
              <div className="audit-metric">
                <div className="metric-header">
                  <span className="metric-label">Evidence Completeness</span>
                  <span
                    className="metric-value"
                    style={{ color: getComplianceColor(audit_readiness?.evidence_completeness || 0) }}
                  >
                    {audit_readiness?.evidence_completeness || 0}%
                  </span>
                </div>
                <div className="metric-progress">
                  <div
                    className="metric-progress__fill"
                    style={{
                      width: `${audit_readiness?.evidence_completeness || 0}%`,
                      backgroundColor: getComplianceColor(audit_readiness?.evidence_completeness || 0),
                    }}
                  />
                </div>
              </div>

              {/* Readiness Status (based on project progress) */}
              <div className="readiness-status">
                {(() => {
                  const config = getReadinessConfig(audit_readiness?.status)
                  const Icon = config.icon
                  const projectProgress = audit_readiness?.project_progress || 0
                  return (
                    <div
                      className="status-badge"
                      style={{ backgroundColor: `${config.color}15`, color: config.color }}
                      title={`Based on ${projectProgress}% project progress`}
                    >
                      <Icon size={24} />
                      <span>{config.label}</span>
                      <span className="status-progress">({projectProgress}%)</span>
                    </div>
                  )
                })()}
              </div>

              {/* Upcoming Review */}
              <div className="upcoming-review">
                <div className="review-header">
                  <Calendar size={16} />
                  <span>Upcoming Review</span>
                </div>
                {audit_readiness?.upcoming_review ? (
                  <div className="review-info">
                    <span className="review-date">{formatDateShort(audit_readiness.upcoming_review.date)}</span>
                    <span className="review-project">{audit_readiness.upcoming_review.project}</span>
                  </div>
                ) : (
                  <span className="no-review">No scheduled review</span>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Risk Exposure Report */}
      <RiskExposureReport />
    </div>
  )
}

export default ComplianceDashboard
